/**
 * Paige Review findings as viewer regions.
 *
 * The API gives each finding `anchors`: the rectangles of the text it refers
 * to, in PDF points from the top left of a 1-based page. The viewer wants
 * regions with 0-based pages and `{ origin, size }` rects. An anchor for an
 * image or chart is the review model's estimate (`source: "model"`): a box
 * for a large picture, a single `point` otherwise, passed on as approximate.
 * A finding with no anchors is still passed on, so it stays listed; the
 * viewer shows it without a place on the page.
 */

var SEVERITIES = ["critical", "warning", "info"];

function toRect(rect) {
  return {
    origin: { x: rect.x, y: rect.y },
    size: { width: rect.width, height: rect.height },
  };
}

function toAnchor(anchor) {
  var result = {
    pageIndex: anchor.page_number - 1,
    rects: (anchor.rects || []).map(toRect),
  };
  // A single spot instead of an area: the viewer draws it as a pin.
  if (anchor.point) {
    result.point = { x: anchor.point.x, y: anchor.point.y };
  }
  // Estimated by the review model, not read off the page.
  if (anchor.approximate || anchor.source === "model") {
    result.approximate = true;
  }
  if (anchor.label) {
    result.label = anchor.label;
  }
  return result;
}

function hasPlace(anchor) {
  return (
    anchor &&
    anchor.page_number &&
    ((anchor.rects && anchor.rects.length > 0) || anchor.point)
  );
}

/** True when any of a finding's anchors is the model's estimate. */
export function isApproximatelyLocated(finding) {
  return (finding.anchors || []).some(function (anchor) {
    return (
      hasPlace(anchor) && (anchor.approximate || anchor.source === "model")
    );
  });
}

export function findingToRegion(finding) {
  var region = {
    id: String(finding.id),
    pageIndex: finding.page_number ? finding.page_number - 1 : 0,
    severity:
      SEVERITIES.indexOf(finding.severity) === -1 ? "info" : finding.severity,
    title: finding.category || finding.rule_id || "Finding",
    anchors: (finding.anchors || []).filter(hasPlace).map(toAnchor),
  };
  if (finding.code_section) {
    region.kind = finding.code_section;
  }
  if (finding.description) {
    region.description = finding.description;
  }
  if (finding.metadata && finding.metadata.location) {
    region.locationNote = finding.metadata.location;
  }
  return region;
}

export function findingsToRegions(findings) {
  return (findings || []).map(findingToRegion);
}
